import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useViewportContext } from 'components/spreadsheet/contexts/data/useViewportContext'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { clampScrollTop, scrollbarGeometry, scrollTopFromThumb } from 'components/spreadsheet/helpers/scroll'
import { HEADER_HEIGHT } from 'style/constants'

const SCROLLBAR_WIDTH = 12

interface TrackPosition {
  top: number
  right: number
}

/**
 * The grid's vertical scrollbar. Vertical scrolling is virtual (see helpers/scroll.ts), so the
 * browser draws no scrollbar for it; this one sits over the right edge of the rows area, between
 * the header and the footer, and drives the same logical offset as the wheel and the keyboard.
 * Dragging the thumb is 1:1 with the thumb; clicking the track pages towards the pointer; the
 * wheel over it scrolls the grid as it would anywhere else. Like a native scrollbar it never
 * takes focus (the grid's own keyboard handling scrolls), so it is hidden from assistive tech.
 */
const VerticalScrollbar: React.FC = () => {
  const { gridRef, logicalScrollTop, logicalTotalHeight, maxScrollTop, rowsAreaHeight, scrollToLogical } =
    useViewportContext()
  const { uniqueNumber } = useDataContext()
  const trackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ grabOffset: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const [position, setPosition] = useState<TrackPosition | null>(null)

  // Place the track over the right edge of the grid, in the grid wrapper's coordinates, and keep
  // it there as the grid is resized or moved (toolbar shown or hidden, window resized).
  useLayoutEffect(() => {
    const grid = gridRef.current
    // offsetParent is the positioned grid wrapper; jsdom has no layout and reports null, so fall
    // back to the parent element there.
    const parent = (grid?.offsetParent ?? grid?.parentElement) as HTMLElement | null
    if (!grid || !parent) return
    const measure = (): void => {
      setPosition({
        top: grid.offsetTop + grid.clientTop + HEADER_HEIGHT,
        right: parent.clientWidth - (grid.offsetLeft + grid.clientLeft + grid.clientWidth)
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(grid)
    observer.observe(parent)
    return () => observer.disconnect()
  }, [gridRef])

  // The wheel over the scrollbar scrolls the grid too. A native listener, since React's onWheel
  // is passive and could not prevent the page from scrolling instead.
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const onWheel = (event: WheelEvent): void => {
      if (event.deltaY === 0) return
      const next = clampScrollTop(logicalScrollTop + event.deltaY, maxScrollTop)
      // At either end the grid cannot move, so the event is left to the page around it, as the
      // grid's own wheel handler does.
      if (next === logicalScrollTop) return
      event.preventDefault()
      scrollToLogical(next)
    }
    track.addEventListener('wheel', onWheel, { passive: false })
    return () => track.removeEventListener('wheel', onWheel)
    // position and maxScrollTop decide whether the track is mounted at all, so the listener has
    // to be (re)attached when they change.
  }, [logicalScrollTop, scrollToLogical, position, maxScrollTop])

  if (!position || maxScrollTop <= 0 || rowsAreaHeight <= 0) return null

  const trackHeight = rowsAreaHeight
  const { thumbHeight, thumbTop } = scrollbarGeometry(logicalTotalHeight, rowsAreaHeight, logicalScrollTop, trackHeight)

  const onThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    dragRef.current = { grabOffset: event.clientY - event.currentTarget.getBoundingClientRect().top }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    setDragging(true)
  }

  const onThumbPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    const track = trackRef.current
    if (!drag || !track) return
    const nextThumbTop = event.clientY - track.getBoundingClientRect().top - drag.grabOffset
    scrollToLogical(scrollTopFromThumb(nextThumbTop, thumbHeight, trackHeight, maxScrollTop))
  }

  const endDrag = (): void => {
    dragRef.current = null
    setDragging(false)
  }

  // A click on the track outside the thumb pages towards the pointer, like a native scrollbar.
  const onTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || event.target !== event.currentTarget) return
    event.preventDefault()
    event.stopPropagation()
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top
    scrollToLogical(logicalScrollTop + (y < thumbTop ? -rowsAreaHeight : rowsAreaHeight))
  }

  // Stop the grid's document-level click handling from treating a scrollbar click as a click on
  // blank space (which would clear cell, row and column selections).
  const swallow = (event: React.SyntheticEvent): void => event.stopPropagation()

  return (
    <div
      ref={trackRef}
      data-testid="vertical-scrollbar"
      data-grid={`data-view-${uniqueNumber}`}
      aria-hidden="true"
      className="absolute z-[8] select-none touch-none"
      style={{ top: position.top, right: position.right, width: SCROLLBAR_WIDTH, height: trackHeight }}
      onPointerDown={onTrackPointerDown}
      onMouseDown={swallow}
      onClick={swallow}
    >
      <div
        data-testid="vertical-scrollbar-thumb"
        className={`absolute left-[3px] right-[3px] rounded-full transition-colors ${
          dragging ? 'bg-muted/70' : 'bg-muted/40 hover:bg-muted/60'
        }`}
        style={{ top: thumbTop, height: thumbHeight }}
        onPointerDown={onThumbPointerDown}
        onPointerMove={onThumbPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      />
    </div>
  )
}

export default VerticalScrollbar
